import {
  Badge,
  Box,
  Button,
  Card,
  CardHeader,
  Flex,
  Grid,
  Heading,
  Select,
  Skeleton,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { useWindowWidth } from '@react-hook/window-size'
import type { ClientAdditionalInfoDto, InsuranceParamsLeasingDto } from 'BrokerTsLib/src/dtos'
import { Risk, Selection } from 'BrokerTsLib/src/enums'
import uniq from 'lodash/uniq'
import { useEffect, useRef, useState } from 'react'

import { LoadingOverlay } from '@/components'
import { BmwPriceChangedDialog } from '@/components/BmwPriceChangedDialog'
import { ConfirmErrorDialog } from '@/components/ConfirmErrorDialog'
import { LeasingBasicParametersFormWrapper } from '@/components/LeasingBasicParametersFormWrapper'
import { LeasingDriverInfoDialog } from '@/components/LeasingDriverInfoDialog'
import { LeasingHeader } from '@/components/LeasingHeader'
import { PageSection as PageLayout } from '@/components/PageSection'
import { QuoteItem } from '@/components/QuoteItem'
import { QuotesFilter } from '@/components/QuotesFilter'
import { BmwContinueMessageDialog } from '@/components/Steps/BmwContinueMessageDialog'
import { BadgeStep } from '@/components/ui/BadgeStep'
import { Container } from '@/components/ui/Container'
import { Section } from '@/components/ui/Section'
import { WizzardHeader } from '@/components/WizzardHeader'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { SortBy } from '@/context/OffersProvider'
import {
  useOffersVehicleContext
} from '@/context/OffersVehicleProvider'
import { usePartner } from '@/context/PartnerContext'
import { usePrintContext } from '@/context/PrintContext'
import { useConfirmOffers } from '@/hooks/useConfirmOffers'
import { useInvokeBmw } from '@/hooks/useOffer'
import { useSaveInsuranceParams } from '@/hooks/useWizard'
import { Check } from '@/icons'
import { scrollToElement } from '@/utils/scrollToElement'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { Market } from 'BrokerTsLib/src/enums'

export const LeasingPageComponent = () => {
  const { translate } = useTranslationsContext()
  const { insuranceParams: ip, refetchInsuranceParams, insuranceParamsId } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsLeasingDto
  const { printMode, type } = usePrintContext()
  const { mutateAsync: invokeBmw } = useInvokeBmw()
  const {
    confirmOffers,
    isSubmitting: isConfirming,
    errorDialogState,
    priceChangeDialogState,
    setIsSubmitting,
    setErrorMessages,
  } = useConfirmOffers()
  const { isOpen: isOpenBmwContinueMessage, onOpen: onOpenBmwContinueMessage, onClose: onCloseBmwContinueMessage } = useDisclosure()
  const { isOpen: isOpenDriverInfo, onOpen: onOpenDriverInfo, onClose: onCloseDriverInfo } = useDisclosure()
  const { currentPartner } = usePartner()
  const isSkMarket = currentPartner?.market === Market.SK
  const { mutateAsync: saveInsuranceParams, isPending: isSavingDriverInfo } = useSaveInsuranceParams()
  const pendingCalculation = useRef(false)

  const {
    refetchOffers: getOffers,
    setOfferIncluded,
    offers,
    isLoading,
    calculatingOffersIds,
    sortBy,
    setSortBy: changeSortBy,
    showAll,
    setShowAll: changeShowAll,
    filtered,
  } = useOffersVehicleContext()

  const [defaultFullHeight, setDefaultFullHeight] = useState<number | undefined>(148 + 32)
  const [showFilter, setShowFilter] = useState(false)
  const [calculationState, setCalculationState] = useState<{
    isCalculated: boolean
    isCalculating: boolean
  }>({ isCalculated: false, isCalculating: false })
  const isCalculatingAny = isLoading || (calculatingOffersIds?.length ?? 0) > 0

  const headerRef = useRef<HTMLDivElement>(null)
  const windowWidth = useWindowWidth()

  // pro pouziti useEfectu nize
  const allOffersRiskVariants = uniq(
    // distinct
    (insuranceParams?.baseParams?.variants ?? []).reduce(
      (pn: number[], insuranceVariant) => [
        ...pn,
        ...(insuranceVariant.variantParts
          ?.filter((x) =>
            [Risk.POV, Risk.HAV, Risk.SPOL, Risk.GAP].includes(x.risk),
          )
          .map((x) => x.riskVariant ?? 0) ?? []),
      ],
      [],
    ), // selectMany
  )

  // pri docteni nove offery s novou riskVariantou musim refetchnout paramsy, aby se spravne zobrazil filter (filtr se plni na zaklade vygenerovanych variant, ktere jsou na paramsech)
  useEffect(() => {
    if (allOffersRiskVariants?.length > 0) refetchInsuranceParams()
  }, [allOffersRiskVariants?.length])

  // při kalkulaci refetchout parametry, aby se načetly změny způsobené kalkulací
  useEffect(() => {
    if (calculationState.isCalculating || calculationState.isCalculated)
      refetchInsuranceParams()
  }, [calculationState.isCalculating])

  useEffect(() => {
    if (isSkMarket && !isDriverInfoFilled) {
      onOpenDriverInfo()
    } else if (insuranceParams?.generalParams.lastWizardStep == 2) {
      handleCalculate()
    }
  }, [])

  useEffect(() => {
    const element = headerRef?.current
    const height = element?.getBoundingClientRect().height
    const top = element?.getBoundingClientRect().top

    if (height) setDefaultFullHeight(height + Number(top))
  }, [headerRef])

  useEffect(() => {
    if (windowWidth > 768) {
      // medium breakpoint
      document.body.style.overflow = 'auto'
      return
    }

    document.body.style.overflow = showFilter ? 'hidden' : 'auto'
  }, [showFilter, windowWidth])

  useEffect(() => {
    if (offers.length > 0) {
      setCalculationState({
        isCalculated: true,
        isCalculating: false,
      })
    }
  }, [offers.length, isCalculatingAny])

  const [firstLoad, setFirstLoad] = useState(true)
  useEffect(() => {
    if (firstLoad) setFirstLoad(false)
    else return

    if (insuranceParams?.generalParams.existsOffers)
      getOffers({})

    setTimeout(() => {
      scrollToOffers()
    }, 100)
  }, [insuranceParams?.generalParams.existsOffers, firstLoad])

  const handleShowFilter = () => {
    setShowFilter(!showFilter)
  }

  const handleAddNewVersion = () => {
    scrollToElement({ elementId: 'sekce-1' })
  }

  const scrollToOffers = () => {
    scrollToElement({ elementId: 'sekce-2', offset: 64 })
  }

  const driverInfo = insuranceParams?.leasingData?.clientAdditionalInfo
  const isDriverInfoFilled =
    (driverInfo?.claims != null && driverInfo?.claims !== undefined) &&
    (insuranceParams.client?.type != Selection.Person || (driverInfo?.driverYoungerThan24 != null && driverInfo?.driverYoungerThan24 !== undefined))

  const calculate = () => {
    setCalculationState({
      isCalculated: false,
      isCalculating: true,
    })

    getOffers({ force: true }).then(({ data }) => {
      if (data?.length == 0) {
        setCalculationState({
          isCalculated: true,
          isCalculating: false,
        })
      }
    })

    setTimeout(() => {
      scrollToOffers()
    }, 100)
  }

  const handleCalculate = () => {
    if (isSkMarket && !isDriverInfoFilled) {
      pendingCalculation.current = true
      onOpenDriverInfo()
      return
    }

    calculate()
  }

  const handleOpenDriverInfoFromHeader = () => {
    pendingCalculation.current = false
    onOpenDriverInfo()
  }

  const handleCloseDriverInfo = () => {
    if (isSkMarket && !isDriverInfoFilled) return
    pendingCalculation.current = false
    onCloseDriverInfo()
  }

  const handleDriverInfoSave = async (data: ClientAdditionalInfoDto) => {
    await saveInsuranceParams({
      ...insuranceParams,
      leasingData: {
        ...insuranceParams?.leasingData,
        clientAdditionalInfo: data,
      },
    })
    onCloseDriverInfo()
    await refetchInsuranceParams()

    if (pendingCalculation.current) {
      pendingCalculation.current = false
      calculate()
    } else if (offers.length > 0) {
      calculate()
    }
  }

  const selectedOffer = filtered.find((quote) => quote.offerId == insuranceParams?.generalParams.selectedOfferId)
  const selectedOffers = offers.filter(x => x.selected)

  return (
    <>
      {printMode && <WizzardHeader />}

      {!printMode && (
        <LeasingHeader
          ref={headerRef}
          brand={insuranceParams?.vehicle.znacka}
          model={insuranceParams?.vehicle.model}
          onOpenDriverInfo={isSkMarket ? handleOpenDriverInfoFromHeader : undefined}
        />
      )}

      {/* Header height placeholder */}
      {!printMode && (
        <Box
          sx={{
            display: { base: 'none', md: 'block' },
            position: 'static',
            pointerEvents: 'none',
            transition: 'all 200ms ease-in-out',
          }}
          style={{
            height: `${defaultFullHeight}px`,
          }}
        />
      )}

      <Section>
        <Container>
          <Grid
            sx={{
              position: 'relative',
              gridTemplateColumns: { md: '250px 2rem 1fr' },
              gap: 0,
            }}
          >
            <Grid
              as="aside"
              sx={{ position: 'relative', alignContent: 'start' }}
            >
              {calculationState.isCalculated && (
                <Grid
                  sx={{
                    position: { md: 'sticky' },
                    top: 28,
                    maxH: { md: '100vh' },
                    py: 4,
                  }}
                  style={{
                    marginTop:
                      windowWidth > 768
                        ? (document.getElementById('sekce-2')?.offsetTop ?? 0)
                        : '0',
                  }}
                >
                  <QuotesFilter
                    showFilter={showFilter}
                    onCloseClick={handleShowFilter}
                  />
                </Grid>
              )}
            </Grid>

            <Box
              sx={{
                width: '3px',
                height: '100%', // 8px is the top margin of the line
                bg: 'white',
                // mt: 2,
                ml: '-3px',
                borderRadius: 'full',
              }}
            />

            <Grid sx={{ alignContent: 'start', alignItems: 'start', gap: 8 }}>
              {!printMode && (
                <PageLayout
                  id="sekce-1"
                  isLast={
                    !calculationState.isCalculated &&
                    !calculationState.isCalculating
                  }
                >
                  {insuranceParams && (
                    <LeasingBasicParametersFormWrapper
                      insuranceParams={insuranceParams}
                      onSubmit={handleCalculate}
                    />
                  )}
                </PageLayout>
              )}

              {(calculationState.isCalculated === true || calculationState.isCalculating === true) && (
                <PageLayout id="sekce-2" mainSx={{ gap: 8 }} isLast>
                  {!printMode && <>
                    <Card>
                      <CardHeader sx={{ py: 5 }}>
                        <Flex
                          sx={{
                            gap: { base: 2, md: 8 },
                            flexWrap: 'wrap',
                            alignItems: 'center',
                          }}
                        >
                          <BadgeStep>
                            {isCalculatingAny ? <Spinner size="xs" /> : 2}
                          </BadgeStep>

                          <Heading
                            sx={{
                              minWidth: '170px', // width of element when calculating text is shown
                              fontSize: 'lg',
                              fontWeight: 'bold',
                            }}
                          >
                            {isCalculatingAny
                              ? translate('leasingSimpleWizard.calculatingLabel', 'Kalkuluji pojištění') + '...'
                              : translate('leasingSimpleWizard.calculationsLabel', 'Kalkulace pojištění')}
                          </Heading>

                          <Skeleton isLoaded={calculationState.isCalculated}>
                            <Box>
                              <Select
                                variant="filled"
                                size="sm"
                                value={sortBy}
                                onChange={(e) =>
                                  changeSortBy!(e.target.value as SortBy)
                                }
                              >
                                <option value={SortBy.Price}>
                                  {translate('sortByPrice', 'Dle pojistného')}
                                </option>
                                <option value={SortBy.Insurer}>
                                  {translate('sortByInsurer', 'Dle pojišťovny')}
                                </option>
                              </Select>
                            </Box>
                          </Skeleton>

                          <Grid
                            sx={{
                              placeContent: 'center',
                              placeItems: 'center',
                            }}
                          >
                            <Skeleton
                              isLoaded={calculationState.isCalculated}
                            >
                              <Badge
                                sx={{ fontSize: 'xs', fontWeight: 'bold' }}
                              >
                                {translate('showed', 'zobrazeno')} {filtered.length}/{offers.length}
                              </Badge>
                            </Skeleton>

                            {/* <Text sx={{ fontSize: 'xs' }}>zobrazeno/celkem</Text> */}
                          </Grid>

                          <Flex
                            sx={{
                              gap: 4,
                              alignItems: 'center',
                              ml: { md: 'auto' },
                            }}
                          >
                            <Skeleton
                              isLoaded={calculationState.isCalculated}
                            >
                              <Button
                                variant="link"
                                onClick={() => changeShowAll(!showAll)}
                              >
                                {showAll ? translate('leasingSimpleWizard.hideUnselected', 'Skrýt nevybrané') : translate('leasingSimpleWizard.showAll', 'Zobrazit vše')}
                              </Button>
                            </Skeleton>

                            <Box>
                              <Skeleton
                                isLoaded={calculationState.isCalculated}
                              >
                                <Button onClick={handleAddNewVersion}>
                                  {translate('step2.addNewVariant', 'Přidat další variantu pojištění')}
                                </Button>
                              </Skeleton>
                            </Box>
                          </Flex>
                        </Flex>
                      </CardHeader>
                    </Card>


                    {filtered.length == 0 && (
                      <Box sx={{ minHeight: '80vh' }}>
                        {calculationState.isCalculated && !calculationState.isCalculating && (
                          <Text fontWeight="bold" textAlign={'center'}>
                            {translate('leasingSimpleWizard.noOffers', 'Pro vybrané kritéria nebyly zkalkulováný žádné nabídky')}
                          </Text>
                        )}
                      </Box>
                    )}
                  </>}

                  {calculationState.isCalculated &&
                    filtered &&
                    filtered.length > 0 &&
                    [
                      ...filtered.filter(x => x.offerId == selectedOffer?.offerId),
                      ...filtered.filter(x => x.offerId != selectedOffer?.offerId)
                    ]
                      .map((offer) => {
                        return (
                          <LoadingOverlay key={offer.offerId}>
                            <QuoteItem
                              isSelected={
                                insuranceParams?.generalParams.selectedOfferId ==
                                offer.offerId
                              }
                              offerId={offer.offerId}
                              onIncludedChange={setOfferIncluded}
                            />
                          </LoadingOverlay>
                        )
                      })}

                  {selectedOffers.length > 0 && (
                    <Flex
                      justifyContent='center'>
                      <Button
                        colorScheme="green"
                        size="lg"
                        isLoading={isConfirming}
                        onClick={() => {
                          onOpenBmwContinueMessage()
                        }}>
                        <Check color="white.500" />
                        {translate('leasingSimpleWizard.contractSelected', 'Sjednat vybrané')}
                      </Button>
                    </Flex>
                  )}
                </PageLayout>
              )}
            </Grid>
          </Grid>
        </Container>
      </Section>

      <BmwContinueMessageDialog
        isOpen={isOpenBmwContinueMessage}
        onClose={onCloseBmwContinueMessage}
        selectedOffers={selectedOffers}
        onConfirm={() =>
          confirmOffers(selectedOffers, () =>
            invokeBmw({
              offerId: selectedOffers.length == 1 ? selectedOffers[0].offerId : '',
              action: 'Continue',
              paramsId: insuranceParamsId!,
            }).catch((errs: any) => {
              const responseData = errs.response?.data ?? []

              if (responseData.length > 0)
                setErrorMessages(
                  responseData.map((e: any) =>
                    e.exception?.message ? e.exception.message : e.message,
                  ),
                )

              setIsSubmitting(false)
            })
          )
        }
      />

      <ConfirmErrorDialog
        isOpen={errorDialogState.isOpen}
        errorMessages={errorDialogState.messages}
        onClose={errorDialogState.onClose}
      />

      <BmwPriceChangedDialog
        isOpen={priceChangeDialogState.isOpen}
        priceError={priceChangeDialogState.message}
        onClose={priceChangeDialogState.onClose}
        onConfirm={priceChangeDialogState.onConfirm}
      />

      <LeasingDriverInfoDialog
        isOpen={isOpenDriverInfo}
        onClose={handleCloseDriverInfo}
        onSave={handleDriverInfoSave}
        isSaving={isSavingDriverInfo}
        initialValues={insuranceParams?.leasingData?.clientAdditionalInfo}
        required={isSkMarket && !isDriverInfoFilled}
      />
    </>
  )
}